import type {
  ABTestVariant,
  ABTestMetrics,
  DashboardLayout,
} from '../store/slices/dashboardPersonalizationSlice';

export interface ABTest {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  startDate: string;
  endDate?: string;
  variants: ABTestVariant[];
  controlGroup: string; // ID of control variant
  targetMetrics: string[];
  minimumSampleSize: number;
  confidenceLevel: number;
  trafficAllocation: number; // 0-1, percentage of users to include
}

export interface ABTestAssignment {
  userId: string;
  testId: string;
  variantId: string;
  assignedAt: string;
  isControlGroup: boolean;
}

export interface ABTestEvent {
  userId: string;
  testId: string;
  variantId: string;
  eventType: string;
  eventData?: Record<string, any>;
  timestamp: string;
}

export interface ABTestResults {
  testId: string;
  variants: Array<{
    variantId: string;
    metrics: ABTestMetrics;
    sampleSize: number;
    conversionRate: number;
    confidenceInterval: [number, number];
    isSignificant: boolean;
    pValue: number;
  }>;
  winner?: string;
  recommendation: string;
  statisticalPower: number;
}

/**
 * A/B Testing Manager for dashboard layouts and features
 */
export class ABTestingManager {
  private activeTests: Map<string, ABTest> = new Map();
  private userAssignments: Map<string, ABTestAssignment[]> = new Map();
  private events: ABTestEvent[] = [];

  /**
   * Initialize with active tests
   */
  constructor(activeTests: ABTest[] = []) {
    activeTests.forEach(test => {
      this.activeTests.set(test.id, test);
    });
  }

  /**
   * Assign user to A/B test variant
   */
  assignUserToTest(userId: string, testId: string): ABTestAssignment | null {
    const test = this.activeTests.get(testId);
    if (!test || !test.isActive) {
      return null;
    }

    // Check if user is already assigned
    const existingAssignment = this.getUserAssignment(userId, testId);
    if (existingAssignment) {
      return existingAssignment;
    }

    // Check if user should be included in test (traffic allocation)
    const userHash = this.hashUserId(userId);
    const trafficThreshold = test.trafficAllocation * 100;
    if (userHash % 100 >= trafficThreshold) {
      return null; // User not included in test
    }

    // Assign to variant based on hash
    const variantIndex = userHash % test.variants.length;
    const variant = test.variants[variantIndex];

    const assignment: ABTestAssignment = {
      userId,
      testId,
      variantId: variant.id,
      assignedAt: new Date().toISOString(),
      isControlGroup: variant.id === test.controlGroup,
    };

    // Store assignment
    const userAssignments = this.userAssignments.get(userId) || [];
    userAssignments.push(assignment);
    this.userAssignments.set(userId, userAssignments);

    return assignment;
  }

  /**
   * Get user's assignment for a specific test
   */
  getUserAssignment(userId: string, testId: string): ABTestAssignment | null {
    const assignments = this.userAssignments.get(userId) || [];
    return assignments.find(a => a.testId === testId) || null;
  }

  /**
   * Get all active assignments for a user
   */
  getUserAssignments(userId: string): ABTestAssignment[] {
    return this.userAssignments.get(userId) || [];
  }

  /**
   * Track A/B test event
   */
  trackEvent(event: Omit<ABTestEvent, 'timestamp'>): void {
    const fullEvent: ABTestEvent = {
      ...event,
      timestamp: new Date().toISOString(),
    };

    this.events.push(fullEvent);

    // Update variant metrics
    const test = this.activeTests.get(event.testId);
    if (test) {
      const variant = test.variants.find(v => v.variantId === event.variantId);
      if (variant) {
        this.updateVariantMetrics(variant, event.eventType, event.eventData);
      }
    }
  }

  /**
   * Get dashboard layout with A/B test modifications
   */
  getTestLayout(userId: string, baseLayout: DashboardLayout): DashboardLayout {
    const assignments = this.getUserAssignments(userId);
    let modifiedLayout = { ...baseLayout };

    // Apply modifications from all active test assignments
    assignments.forEach(assignment => {
      const test = this.activeTests.get(assignment.testId);
      if (test && test.isActive) {
        const variant = test.variants.find(v => v.id === assignment.variantId);
        if (variant && variant.layoutChanges) {
          modifiedLayout = this.applyLayoutChanges(
            modifiedLayout,
            variant.layoutChanges
          );
        }
      }
    });

    return modifiedLayout;
  }

  /**
   * Calculate A/B test results
   */
  calculateResults(testId: string): ABTestResults | null {
    const test = this.activeTests.get(testId);
    if (!test) return null;

    const testEvents = this.events.filter(e => e.testId === testId);
    const variants = test.variants.map(variant => {
      const variantEvents = testEvents.filter(e => e.variantId === variant.id);
      const sampleSize = new Set(variantEvents.map(e => e.userId)).size;

      const conversions = variantEvents.filter(
        e => e.eventType === 'conversion' || e.eventType === 'goal_completed'
      ).length;

      const conversionRate = sampleSize > 0 ? conversions / sampleSize : 0;

      // Simple confidence interval calculation (would use proper statistical methods in production)
      const standardError = Math.sqrt(
        (conversionRate * (1 - conversionRate)) / sampleSize
      );
      const marginOfError = 1.96 * standardError; // 95% confidence
      const confidenceInterval: [number, number] = [
        Math.max(0, conversionRate - marginOfError),
        Math.min(1, conversionRate + marginOfError),
      ];

      return {
        variantId: variant.id,
        metrics: variant.metrics,
        sampleSize,
        conversionRate,
        confidenceInterval,
        isSignificant: sampleSize >= test.minimumSampleSize,
        pValue: this.calculatePValue(
          variant,
          test.variants.find(v => v.id === test.controlGroup)!
        ),
      };
    });

    // Determine winner
    const significantVariants = variants.filter(
      v => v.isSignificant && v.pValue < 0.05
    );
    const winner =
      significantVariants.length > 0
        ? significantVariants.reduce((best, current) =>
            current.conversionRate > best.conversionRate ? current : best
          ).variantId
        : undefined;

    return {
      testId,
      variants,
      winner,
      recommendation: this.generateRecommendation(variants, winner),
      statisticalPower: this.calculateStatisticalPower(variants),
    };
  }

  /**
   * Add new A/B test
   */
  addTest(test: ABTest): void {
    this.activeTests.set(test.id, test);
  }

  /**
   * Stop A/B test
   */
  stopTest(testId: string): void {
    const test = this.activeTests.get(testId);
    if (test) {
      test.isActive = false;
      test.endDate = new Date().toISOString();
    }
  }

  /**
   * Get active tests
   */
  getActiveTests(): ABTest[] {
    return Array.from(this.activeTests.values()).filter(test => test.isActive);
  }

  /**
   * Hash user ID for consistent assignment
   */
  private hashUserId(userId: string): number {
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Apply layout changes from A/B test variant
   */
  private applyLayoutChanges(
    layout: DashboardLayout,
    changes: Partial<DashboardLayout>
  ): DashboardLayout {
    return {
      ...layout,
      ...changes,
      sections: changes.sections || layout.sections,
    };
  }

  /**
   * Update variant metrics based on event
   */
  private updateVariantMetrics(
    variant: ABTestVariant,
    eventType: string,
    eventData?: Record<string, any>
  ): void {
    switch (eventType) {
      case 'impression':
        variant.metrics.impressions += 1;
        break;
      case 'interaction':
        variant.metrics.interactions += 1;
        break;
      case 'conversion':
        variant.metrics.conversionRate =
          (variant.metrics.conversionRate * variant.metrics.impressions + 1) /
          (variant.metrics.impressions + 1);
        break;
      case 'engagement':
        if (eventData?.score) {
          variant.metrics.engagementScore =
            (variant.metrics.engagementScore + eventData.score) / 2;
        }
        break;
      case 'retention':
        if (eventData?.retained) {
          variant.metrics.retentionRate =
            (variant.metrics.retentionRate + (eventData.retained ? 1 : 0)) / 2;
        }
        break;
      case 'satisfaction':
        if (eventData?.rating) {
          variant.metrics.userSatisfaction =
            (variant.metrics.userSatisfaction + eventData.rating) / 2;
        }
        break;
    }
  }

  /**
   * Calculate p-value (simplified version)
   */
  private calculatePValue(variant: any, control: any): number {
    // This is a simplified calculation - use proper statistical libraries in production
    const n1 = variant.sampleSize;
    const n2 = control.sampleSize;
    const p1 = variant.conversionRate;
    const p2 = control.conversionRate;

    if (n1 === 0 || n2 === 0) return 1;

    const pooledP = (p1 * n1 + p2 * n2) / (n1 + n2);
    const standardError = Math.sqrt(
      pooledP * (1 - pooledP) * (1 / n1 + 1 / n2)
    );
    const zScore = Math.abs(p1 - p2) / standardError;

    // Approximate p-value calculation
    return 2 * (1 - this.normalCDF(zScore));
  }

  /**
   * Normal cumulative distribution function approximation
   */
  private normalCDF(x: number): number {
    return 0.5 * (1 + this.erf(x / Math.sqrt(2)));
  }

  /**
   * Error function approximation
   */
  private erf(x: number): number {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = x >= 0 ? 1 : -1;
    x = Math.abs(x);

    const t = 1.0 / (1.0 + p * x);
    const y =
      1.0 -
      ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  }

  /**
   * Generate recommendation based on results
   */
  private generateRecommendation(variants: any[], winner?: string): string {
    if (!winner) {
      return 'No statistically significant winner found. Continue testing or increase sample size.';
    }

    const winnerVariant = variants.find(v => v.variantId === winner);
    if (!winnerVariant) {
      return 'Unable to generate recommendation.';
    }

    const improvement =
      ((winnerVariant.conversionRate - variants[0].conversionRate) /
        variants[0].conversionRate) *
      100;

    return (
      `Variant ${winner} shows a ${improvement.toFixed(1)}% improvement in conversion rate. ` +
      `Recommend implementing this variant for all users.`
    );
  }

  /**
   * Calculate statistical power
   */
  private calculateStatisticalPower(variants: any[]): number {
    // Simplified calculation - use proper statistical methods in production
    const totalSampleSize = variants.reduce((sum, v) => sum + v.sampleSize, 0);
    const minRequiredSample = 1000; // Minimum sample size for 80% power

    return Math.min(1, totalSampleSize / minRequiredSample);
  }
}

/**
 * Default A/B tests for dashboard layouts
 */
export const defaultABTests: ABTest[] = [
  {
    id: 'dashboard_layout_test_1',
    name: 'Beginner vs Advanced Layout',
    description:
      'Test whether beginners prefer detailed explanations or compact layouts',
    isActive: true,
    startDate: new Date().toISOString(),
    variants: [
      {
        id: 'control_default',
        name: 'Default Layout',
        description: 'Current default dashboard layout',
        layoutChanges: {},
        trafficAllocation: 0.5,
        isActive: true,
        metrics: {
          impressions: 0,
          interactions: 0,
          conversionRate: 0,
          engagementScore: 0,
          retentionRate: 0,
          userSatisfaction: 0,
        },
      },
      {
        id: 'variant_beginner_focused',
        name: 'Beginner Focused',
        description: 'Layout optimized for beginners with more explanations',
        layoutChanges: {
          id: 'beginner_focused',
          name: 'Beginner Focused Test',
        },
        trafficAllocation: 0.5,
        isActive: true,
        metrics: {
          impressions: 0,
          interactions: 0,
          conversionRate: 0,
          engagementScore: 0,
          retentionRate: 0,
          userSatisfaction: 0,
        },
      },
    ],
    controlGroup: 'control_default',
    targetMetrics: ['engagement', 'retention', 'satisfaction'],
    minimumSampleSize: 100,
    confidenceLevel: 0.95,
    trafficAllocation: 0.2, // 20% of users
  },
  {
    id: 'tips_placement_test',
    name: 'Tips Placement Test',
    description: 'Test optimal placement of contextual tips',
    isActive: true,
    startDate: new Date().toISOString(),
    variants: [
      {
        id: 'tips_top',
        name: 'Tips at Top',
        description: 'Show tips at the top of dashboard',
        layoutChanges: {
          sections: [
            {
              id: 'tips',
              type: 'tips' as const,
              title: 'Tips',
              priority: 1,
              visible: true,
              collapsed: false,
              position: 1,
              loadingStrategy: 'eager' as const,
              cacheStrategy: 'memory' as const,
              customization: {
                showExplanations: true,
                showConfidenceIndicators: false,
                maxItems: 2,
                sortBy: 'priority' as const,
                filterBy: [],
                displayStyle: 'card' as const,
              },
            },
          ],
        },
        trafficAllocation: 0.5,
        isActive: true,
        metrics: {
          impressions: 0,
          interactions: 0,
          conversionRate: 0,
          engagementScore: 0,
          retentionRate: 0,
          userSatisfaction: 0,
        },
      },
      {
        id: 'tips_inline',
        name: 'Tips Inline',
        description: 'Show tips inline with other content',
        layoutChanges: {},
        trafficAllocation: 0.5,
        isActive: true,
        metrics: {
          impressions: 0,
          interactions: 0,
          conversionRate: 0,
          engagementScore: 0,
          retentionRate: 0,
          userSatisfaction: 0,
        },
      },
    ],
    controlGroup: 'tips_inline',
    targetMetrics: ['interaction', 'engagement'],
    minimumSampleSize: 50,
    confidenceLevel: 0.95,
    trafficAllocation: 0.1, // 10% of users
  },
];

/**
 * Create A/B testing manager instance
 */
export function createABTestingManager(
  tests: ABTest[] = defaultABTests
): ABTestingManager {
  return new ABTestingManager(tests);
}
